import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setFilters } from '../../RTK/dashboard/Dashboard';
import { Filter, Calendar, CheckCircle, CreditCard, Edit3 } from 'lucide-react';

const DashboardFilters = () => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.dashboard);
  const [showCustomDays, setShowCustomDays] = useState(false);
  const [customDays, setCustomDays] = useState('');

  // Check if current value is custom
  const isCustomValue = typeof filters.days === 'number' && ![7, 30, 90].includes(filters.days);

  const handleFilterChange = (key, value) => {
    if (key === 'days' && value === 'custom') {
      // فقط اعرض الـ popup، لا تغير القيمة فوراً
      setCustomDays('');
      setShowCustomDays(true);
      return;
    }
    if (key === 'days' && value !== 'custom') {
      setShowCustomDays(false);
      setCustomDays('');
    }
    dispatch(setFilters({ [key]: value }));
  };

  const handleCustomDaysSubmit = () => {
    const days = parseInt(customDays);
    if (days && days > 0 && days <= 365) {
      dispatch(setFilters({ days: days }));
      setShowCustomDays(false);
      setCustomDays('');
    }
  };

  const handleCustomDaysCancel = () => {
    setShowCustomDays(false);
    setCustomDays('');
    // إذا مكانش في قيمة مخصصة، ارجع للقيمة الافتراضية
    if (!isCustomValue) {
      dispatch(setFilters({ days: 7 }));
    }
  };

  const timeOptions = [
    { value: 7, label: '7 أيام', icon: '📅' },
    { value: 30, label: '30 يوم', icon: '🗓️' },
    { value: 90, label: '90 يوم', icon: '📊' },
    { value: 'custom', label: 'مخصص', icon: '⚙️' },
  ];

  const orderStatusOptions = [
    { value: 'pending', label: 'قيد الانتظار', icon: '⏳' },
    { value: 'completed', label: 'مكتمل', icon: '✅' },
    { value: 'cancelled', label: 'ملغي', icon: '❌' },
  ];

  const paymentStatusOptions = [
    { value: 'initial', label: 'أولي', icon: '🔄' },
    { value: 'paid', label: 'مدفوع', icon: '💰' },
    { value: 'failed', label: 'فشل', icon: '🚫' },
  ];

  const FilterSelect = ({ value, onChange, options, placeholder, icon: Icon }) => (
    <div className="relative group">
      <Icon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-yellow-400 transition-colors w-4 h-4 z-10" />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none bg-gradient-to-r from-gray-800/80 to-gray-700/80 backdrop-blur-sm text-gray-100 px-10 py-3 pr-12 rounded-xl border border-gray-600/50 focus:border-yellow-500/70 focus:from-gray-700/80 focus:to-gray-600/80 outline-none transition-all duration-300 cursor-pointer hover:border-gray-500/70 shadow-lg min-w-[140px]"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} className="bg-gray-800 text-gray-100">
            {option.icon} {option.label}
          </option>
        ))}
      </select>
      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
        <svg className="w-4 h-4 text-gray-400 group-hover:text-yellow-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );

  return (
    <div className="flex flex-wrap gap-4 items-center" dir="rtl">
      <div className="flex items-center gap-2 text-gray-300 font-medium">
        <Filter className="w-5 h-5 text-yellow-400" />
        <span>الفلاتر:</span>
      </div>
      
      <div className="relative">
        <FilterSelect
          value={isCustomValue ? 'custom' : filters.days}
          onChange={(value) => handleFilterChange('days', value)}
          options={timeOptions}
          placeholder="اختر الفترة"
          icon={Calendar}
        />
        
        {/* Custom Days Input Popup */}
        {showCustomDays && (
          <>
            {/* Backdrop للإغلاق عند النقر خارج الـ popup */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setShowCustomDays(false)}
            ></div>
            
            <div className="absolute top-full mt-2 right-0 z-50 bg-gradient-to-br from-gray-800/95 to-gray-900/95 backdrop-blur-sm border border-gray-600/50 rounded-xl p-4 shadow-2xl min-w-[280px]">
              <div className="flex items-center gap-2 mb-3">
                <Edit3 className="w-4 h-4 text-yellow-400" />
                <span className="text-white font-medium">عدد الأيام المخصص</span>
              </div>
              
              <div className="space-y-3">
                <input
                  type="number"
                  value={customDays}
                  onChange={(e) => setCustomDays(e.target.value)}
                  placeholder="أدخل عدد الأيام"
                  min="1"
                  max="365"
                  className="w-full px-3 py-2 bg-gray-700/80 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:border-yellow-500/70 focus:bg-gray-600/80 outline-none transition-all"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCustomDaysSubmit();
                    }
                    if (e.key === 'Escape') {
                      handleCustomDaysCancel();
                    }
                  }}
                />
                
                <div className="flex gap-2">
                  <button
                    onClick={handleCustomDaysSubmit}
                    disabled={!customDays || parseInt(customDays) <= 0 || parseInt(customDays) > 365}
                    className="flex-1 px-3 py-2 bg-gradient-to-r from-yellow-500/80 to-orange-500/80 hover:from-yellow-500 hover:to-orange-500 disabled:from-gray-600/50 disabled:to-gray-700/50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <span>تطبيق</span>
                  </button>
                  <button
                    onClick={handleCustomDaysCancel}
                    className="flex-1 px-3 py-2 bg-gray-600/80 hover:bg-gray-500/80 text-gray-200 rounded-lg font-medium transition-all duration-300"
                  >
                    إلغاء
                  </button>
                </div>
              </div>
              
              <div className="mt-3 text-xs text-gray-400 text-center">
                يمكنك إدخال قيمة من 1 إلى 365 يوم
              </div>
            </div>
          </>
        )}
      </div>

      {/* Display current custom days if selected */}
      {isCustomValue && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500/20 border border-yellow-500/30 rounded-lg text-yellow-400 text-sm font-medium">
          <Calendar className="w-4 h-4" />
          <span>{filters.days} يوم مخصص</span>
          {/* زر تعديل سريع - هنا فقط يفتح الـ popup */}
          <button
            onClick={() => {
              setCustomDays(filters.days.toString());
              setShowCustomDays(true);
            }}
            className="ml-2 p-1 hover:bg-yellow-500/20 rounded transition-colors"
            title="تعديل القيمة"
          >
            <Edit3 className="w-3 h-3" />
          </button>
        </div>
      )}

      <FilterSelect
        value={filters.orderStatus}
        onChange={(value) => handleFilterChange('orderStatus', value)}
        options={orderStatusOptions}
        placeholder="حالة الطلب"
        icon={CheckCircle}
      />

      <FilterSelect
        value={filters.paymentStatus}
        onChange={(value) => handleFilterChange('paymentStatus', value)}
        options={paymentStatusOptions}
        placeholder="حالة الدفع"
        icon={CreditCard}
      />
    </div>
  );
};

export default DashboardFilters;